// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { CourseHoleTeeLength } from './useCourseHoles';

/**
 * How far a measured position may sit from a scorecard length and still be that tee.
 *
 * A scorecard length is measured from the middle of the tee box to the middle of the green;
 * a click on satellite imagery lands within a few metres of either. Two per cent covers that
 * on a long hole (9 m on a 470 m par 5) and would be too tight on a short one, so the fixed
 * floor takes over there. Deliberately smaller than the gap between adjacent tee boxes on a
 * real course — a tee that could be either of two is not identified, and is left unnamed.
 */
export const TEE_MATCH_TOLERANCE_METERS = 8;
export const TEE_MATCH_TOLERANCE_FRACTION = 0.02;

const toleranceFor = (lengthMeters: number): number =>
  Math.max(TEE_MATCH_TOLERANCE_METERS, lengthMeters * TEE_MATCH_TOLERANCE_FRACTION);

/**
 * Which tee a position of this length is, according to the scorecard.
 *
 * Returns the closest tee within tolerance, or null — never a best guess. Naming a tee the
 * admin did not name is writing human-looking data from an inference, and the wrong colour on
 * a tee box is worse than no colour at all: nothing on the screen would say it was guessed.
 *
 * Ties (two tees the same distance away, which means two tees with the same card length) go
 * unmatched for the same reason.
 */
export const matchTeeByLength = (
  teeLengths: readonly CourseHoleTeeLength[],
  measuredMeters: number
): CourseHoleTeeLength | null => {
  if (!Number.isFinite(measuredMeters) || measuredMeters <= 0) return null;

  const within = teeLengths
    .map((tee) => ({ tee, delta: Math.abs(tee.lengthMeters - measuredMeters) }))
    .filter(({ tee, delta }) => delta <= toleranceFor(tee.lengthMeters))
    .sort((a, b) => a.delta - b.delta);

  if (within.length === 0) return null;
  if (within.length > 1 && within[0].delta === within[1].delta) return null;
  return within[0].tee;
};

/**
 * The tee whose card length a live measurement is closest to, tolerance ignored.
 *
 * For the aiming line's readout, which shows what the pointer is heading towards while it is
 * still far off — "Yellow 310 · +42" is how an admin knows they are on the wrong tee box, and
 * a label that only appears once they are already within 8 m would tell them nothing on the
 * way there. Never used to name anything; {@link matchTeeByLength} is the one that decides.
 */
export const nearestTeeByLength = (
  teeLengths: readonly CourseHoleTeeLength[],
  measuredMeters: number
): CourseHoleTeeLength | null => {
  if (!Number.isFinite(measuredMeters) || teeLengths.length === 0) return null;
  return [...teeLengths].sort(
    (a, b) =>
      Math.abs(a.lengthMeters - measuredMeters) - Math.abs(b.lengthMeters - measuredMeters)
  )[0];
};

/** One selectable tee: which tee, how long it is, and who says so. */
export type TeeLengthChoice = {
  teeId: string;
  teeName: string;
  lengthMeters: number;
  source: string;
};

/**
 * Source ranking for choosing between two stored lengths for the same tee.
 *
 * Our own measurement first, then a person, then a club's card, then OSM — the order
 * `LENGTH_SOURCE_ORDER` lays its columns out in, kept here as its own list so this module does
 * not depend on a table builder to compare two numbers.
 */
const SOURCE_RANK = ['analysis', 'manual', 'golfisverige', 'golfingholiday', 'osm'];

const rank = (source: string) => {
  const index = SOURCE_RANK.indexOf(source);
  return index < 0 ? SOURCE_RANK.length : index;
};

/**
 * The tees this hole has a stored length for, longest first, one entry per tee.
 *
 * For the hole map's tee chips. A tee measured by several sources appears once, carrying the
 * best-attested of them — the chip has room for one number, and showing the same tee twice
 * would read as two tees rather than as one disagreement.
 *
 * Longest first because that is the order a card is printed and read in: the back tee is the
 * hole as it is built to play, and the forward tees are read as steps down from it.
 */
export const teeLengthChoices = (
  teeLengths: readonly CourseHoleTeeLength[]
): TeeLengthChoice[] => {
  const byTee = new Map<string, CourseHoleTeeLength>();
  for (const length of teeLengths) {
    if (!length.teeId || length.lengthMeters <= 0) continue;
    const held = byTee.get(length.teeId);
    if (held === undefined || rank(length.source) < rank(held.source)) {
      byTee.set(length.teeId, length);
    }
  }

  return [...byTee.values()]
    .map((length) => ({
      teeId: length.teeId,
      teeName: length.teeName,
      lengthMeters: length.lengthMeters,
      source: length.source,
    }))
    .sort((a, b) =>
      b.lengthMeters === a.lengthMeters
        ? a.teeName.localeCompare(b.teeName)
        : b.lengthMeters - a.lengthMeters
    );
};
